import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogHeader,
  DialogBody,
  Card,
  CardBody,
  Typography,
} from '@material-tailwind/react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
  CartesianGrid, ReferenceLine, Cell,
} from 'recharts';

const NUMBER_FMT = new Intl.NumberFormat('en-US');
const fmtNum = (v) => (v != null && !isNaN(v) ? NUMBER_FMT.format(Math.round(v)) : '0');
const fmtPct = (v) => (v != null && !isNaN(v) ? `${(v * 100).toFixed(1)}%` : '0%');

const COLORS = {
  views: '#3b82f6',
  impressions: '#8b5cf6',
  ctr: '#f59e0b',
  calls: '#059669',
  emails: '#0891b2',
  watchlists: '#d946ef',
  texts: '#84cc16',
  positive: '#22c55e',
  negative: '#ef4444',
  peer: '#e5e7eb',
  listing: '#3b82f6',
  featured: '#f59e0b',
};

const parseFileDt = (raw) => {
  if (!raw) return null;
  const [mm, dd, yyyy] = raw.split('_').map(Number);
  return new Date(yyyy, mm - 1, dd);
};

const fmtWeek = (raw) => {
  const dt = parseFileDt(raw);
  if (!dt) return raw;
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-3 text-sm">
      <div className="font-semibold text-gray-700 mb-1">{label}</div>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-gray-500">{entry.name}:</span>
          <span className="font-medium">{typeof entry.value === 'number' && entry.value < 1 && entry.name.includes('CTR') ? fmtPct(entry.value) : fmtNum(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

function WebStatsListingDialog({ open, onClose, listing, loadWeeklyData, peerListings }) {
  const [weeklyData, setWeeklyData] = useState([]);
  const [weeklyLoading, setWeeklyLoading] = useState(false);

  // Load weekly data for this listing via Cloud Function
  useEffect(() => {
    if (!open || !listing) {
      setWeeklyData([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setWeeklyLoading(true);
      try {
        const result = await loadWeeklyData(listing.listing_id);
        if (cancelled) return;
        const weeks = (result || [])
          .map((d) => ({
            ...d,
            _dt: parseFileDt(d.fileDt),
            week: fmtWeek(d.fileDt),
          }))
          .filter((d) => d._dt)
          .sort((a, b) => a._dt - b._dt);
        setWeeklyData(weeks);
      } catch (err) {
        console.error('Failed to load listing detail:', err);
      } finally {
        if (!cancelled) setWeeklyLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, listing, loadWeeklyData]);

  // ─── Chart 1: Views & Impressions over time ─────────────────────────────
  const viewsChartData = useMemo(() => {
    return weeklyData.map((d) => ({
      week: d.week,
      Views: d.details_views || 0,
      Impressions: d.impressions || 0,
    }));
  }, [weeklyData]);

  // ─── Chart 1b: Cumulative running totals ──────────────────────────────────
  const cumulativeChartData = useMemo(() => {
    let totalViews = 0;
    let totalImpressions = 0;
    return weeklyData.map((d) => {
      totalViews += d.details_views || 0;
      totalImpressions += d.impressions || 0;
      return {
        week: d.week,
        'Total Views': totalViews,
        'Total Impressions': totalImpressions,
      };
    });
  }, [weeklyData]);

  // ─── Chart 2: CTR over time ──────────────────────────────────────────────
  const ctrChartData = useMemo(() => {
    return weeklyData.map((d) => ({
      week: d.week,
      CTR: d.click_through_rate || 0,
    }));
  }, [weeklyData]);

  // ─── Chart 3: Engagement over time (calls, emails, watchlists) ──────────
  const engagementData = useMemo(() => {
    return weeklyData.map((d) => ({
      week: d.week,
      Calls: d.total_clicks_to_call || 0,
      Emails: d.email_leads || 0,
      Watchlists: d.total_watchlists || 0,
      Texts: d.total_clicks_to_text || 0,
    }));
  }, [weeklyData]);

  // ─── Chart 4: Week-over-week change in views ────────────────────────────
  const momentumData = useMemo(() => {
    if (weeklyData.length < 2) return [];
    return weeklyData.slice(1).map((d, i) => {
      const prev = weeklyData[i].details_views || 0;
      const curr = d.details_views || 0;
      const delta = curr - prev;
      return { week: d.week, Change: delta };
    });
  }, [weeklyData]);

  // ─── Chart 5: Performance vs peers (same model) ─────────────────────────
  const peerComparison = useMemo(() => {
    if (!listing || !peerListings || peerListings.length === 0) return null;

    const peers = peerListings.filter(
      (p) => String(p.listing_id) !== String(listing.listing_id)
    );
    if (peers.length === 0) return null;

    const peerAvgViews = peers.reduce((s, p) => s + (p.details_views || 0), 0) / peers.length;
    const peerAvgImpr = peers.reduce((s, p) => s + (p.impressions || 0), 0) / peers.length;
    const peerAvgCTR = peers.reduce((s, p) => s + (p.click_through_rate || 0), 0) / peers.length;
    const peerAvgWatchlists = peers.reduce((s, p) => s + (p.total_watchlists || 0), 0) / peers.length;

    return {
      peerCount: peers.length,
      model: listing.model,
      data: [
        {
          metric: 'Views',
          'This Listing': listing.details_views || 0,
          'Peer Avg': Math.round(peerAvgViews),
        },
        {
          metric: 'Impressions',
          'This Listing': listing.impressions || 0,
          'Peer Avg': Math.round(peerAvgImpr),
        },
        {
          metric: 'Watchlists',
          'This Listing': listing.total_watchlists || 0,
          'Peer Avg': Math.round(peerAvgWatchlists),
        },
      ],
      ctr: {
        listing: listing.click_through_rate || 0,
        peerAvg: peerAvgCTR,
      },
    };
  }, [listing, peerListings]);

  // ─── Chart 6: Featured impact (views during featured vs not) ─────────────
  const featuredImpact = useMemo(() => {
    if (!weeklyData.length) return null;
    const featured = weeklyData.filter((d) => (d.is_featured || '').toLowerCase() === 'yes');
    const notFeatured = weeklyData.filter((d) => (d.is_featured || '').toLowerCase() !== 'yes');
    if (featured.length === 0 || notFeatured.length === 0) return null;

    const avgFeatured = featured.reduce((s, d) => s + (d.details_views || 0), 0) / featured.length;
    const avgNotFeatured = notFeatured.reduce((s, d) => s + (d.details_views || 0), 0) / notFeatured.length;
    const lift = avgNotFeatured > 0 ? ((avgFeatured - avgNotFeatured) / avgNotFeatured) * 100 : 0;

    return {
      data: [
        { status: 'Featured', 'Avg Weekly Views': Math.round(avgFeatured) },
        { status: 'Not Featured', 'Avg Weekly Views': Math.round(avgNotFeatured) },
      ],
      featuredWeeks: featured.length,
      notFeaturedWeeks: notFeatured.length,
      lift,
    };
  }, [weeklyData]);

  // ─── Summary stats ───────────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (!weeklyData.length) return null;
    const totalViews = weeklyData.reduce((s, d) => s + (d.details_views || 0), 0);
    const totalImpr = weeklyData.reduce((s, d) => s + (d.impressions || 0), 0);
    const totalCalls = weeklyData.reduce((s, d) => s + (d.total_clicks_to_call || 0), 0);
    const totalEmails = weeklyData.reduce((s, d) => s + (d.email_leads || 0), 0);
    const totalWatchlists = weeklyData.reduce((s, d) => s + (d.total_watchlists || 0), 0);
    const latest = weeklyData[weeklyData.length - 1];

    // Trend: compare last 4 weeks avg to first 4 weeks avg
    const recentWeeks = weeklyData.slice(-4);
    const earlyWeeks = weeklyData.slice(0, 4);
    const recentAvg = recentWeeks.reduce((s, d) => s + (d.details_views || 0), 0) / recentWeeks.length;
    const earlyAvg = earlyWeeks.length > 0
      ? earlyWeeks.reduce((s, d) => s + (d.details_views || 0), 0) / earlyWeeks.length
      : 0;
    const trendPct = earlyAvg > 0 ? ((recentAvg - earlyAvg) / earlyAvg) * 100 : 0;

    return {
      totalViews,
      totalImpr,
      totalCalls,
      totalEmails,
      totalWatchlists,
      weeksTracked: weeklyData.length,
      daysAging: listing?.relist_true_dom || latest.days_aging || 0,
      dailyViews: latest.days_aging > 0 ? totalViews / latest.days_aging : 0,
      relistCount: listing?.relist_count || 1,
      overallCTR: totalImpr > 0 ? totalViews / totalImpr : 0,
      trendPct,
      peakViews: Math.max(...weeklyData.map((d) => d.details_views || 0)),
      peakWeek: weeklyData.reduce((best, d) => (d.details_views || 0) > (best.details_views || 0) ? d : best, weeklyData[0]),
    };
  }, [weeklyData, listing?.relist_true_dom, listing?.relist_count]);

  if (!listing) return null;

  const chartHeight = 180;

  return (
    <Dialog open={open} handler={onClose} size="xxl" className="max-h-[95vh]">
      <DialogHeader className="pb-2 border-b border-gray-100">
        <div className="flex items-center justify-between w-full">
          <div>
            <div className="flex items-center gap-3">
              <Typography variant="h5">
                {listing.year} {listing.manufacturer} {listing.model}
              </Typography>
              {listing.reg && (
                <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                  {listing.reg}
                </span>
              )}
              <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                (listing.status || '').toUpperCase() === 'ACTIVE'
                  ? 'text-green-700 bg-green-50'
                  : 'text-red-700 bg-red-50'
              }`}>
                {listing.status}
              </span>
            </div>
            <div className="flex items-center gap-4 mt-1">
              {listing.contact_name && (
                <Typography variant="small" className="text-gray-700">
                  Contact: <strong>{listing.contact_name}</strong>
                </Typography>
              )}
              {listing.serial_number && (
                <Typography variant="small" className="text-gray-600">
                  S/N: {listing.serial_number}
                </Typography>
              )}
              {listing.listing_url && (
                <a href={listing.listing_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline">
                  View on Controller.com
                </a>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </DialogHeader>

      <DialogBody className="overflow-y-auto max-h-[calc(95vh-80px)] p-4">
        {weeklyLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
          </div>
        )}

        {/* Summary stat pills */}
        {stats && (
          <div className="flex flex-wrap gap-3 mb-5">
            {[
              { label: 'Total Views', value: fmtNum(stats.totalViews) },
              { label: 'Total Impressions', value: fmtNum(stats.totalImpr) },
              { label: 'Daily Avg Views', value: stats.dailyViews.toFixed(1) },
              { label: 'Overall CTR', value: fmtPct(stats.overallCTR) },
              { label: 'Days on Market', value: stats.daysAging },
              { label: 'Weeks Tracked', value: stats.weeksTracked },
              { label: 'Calls', value: stats.totalCalls },
              { label: 'Emails', value: stats.totalEmails },
              { label: 'Watchlists', value: fmtNum(stats.totalWatchlists) },
              {
                label: 'Trend',
                value: `${stats.trendPct > 0 ? '+' : ''}${stats.trendPct.toFixed(0)}%`,
                color: stats.trendPct > 10 ? 'text-green-600 bg-green-50' : stats.trendPct < -10 ? 'text-red-600 bg-red-50' : 'text-gray-600 bg-gray-50',
                tooltip: 'Last 4 weeks vs first 4 weeks avg views',
              },
            ].map((s) => (
              <div key={s.label} className={`px-3 py-1.5 rounded-lg border text-center ${s.color || 'border-gray-200 bg-gray-50'}`} title={s.tooltip || ''}>
                <div className="text-xs text-gray-500 uppercase tracking-wide">{s.label}</div>
                <div className={`text-sm font-semibold ${s.color ? '' : 'text-gray-800'}`}>{s.value}</div>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Chart 1: Views & Impressions trend */}
          <Card className="shadow-sm">
            <CardBody className="p-4">
              <Typography variant="small" className="font-semibold text-gray-700 mb-1">
                Views & Impressions Over Time
              </Typography>
              <Typography variant="small" className="text-gray-600 text-xs mb-3">
                Weekly detail views and search impressions — shows if interest is growing or declining
              </Typography>
              <ResponsiveContainer width="100%" height={chartHeight}>
                <AreaChart data={viewsChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area yAxisId="right" type="monotone" dataKey="Impressions" stroke={COLORS.impressions} fill={COLORS.impressions} fillOpacity={0.08} strokeWidth={1.5} />
                  <Area yAxisId="left" type="monotone" dataKey="Views" stroke={COLORS.views} fill={COLORS.views} fillOpacity={0.15} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>

          {/* Chart 1b: Cumulative running totals */}
          {cumulativeChartData.length > 1 && (
            <Card className="shadow-sm">
              <CardBody className="p-4">
                <Typography variant="small" className="font-semibold text-gray-700 mb-1">
                  Cumulative Exposure
                </Typography>
                <Typography variant="small" className="text-gray-600 text-xs mb-3">
                  Running total of views and impressions — shows growth trajectory and when exposure plateaus
                </Typography>
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <AreaChart data={cumulativeChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="week" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area yAxisId="right" type="monotone" dataKey="Total Impressions" stroke={COLORS.impressions} fill={COLORS.impressions} fillOpacity={0.08} strokeWidth={1.5} />
                    <Area yAxisId="left" type="monotone" dataKey="Total Views" stroke={COLORS.views} fill={COLORS.views} fillOpacity={0.15} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardBody>
            </Card>
          )}

          {/* Chart 2: Weekly momentum (change) */}
          {momentumData.length > 0 && (
            <Card className="shadow-sm">
              <CardBody className="p-4">
                <Typography variant="small" className="font-semibold text-gray-700 mb-1">
                  Weekly Momentum
                </Typography>
                <Typography variant="small" className="text-gray-600 text-xs mb-3">
                  Week-over-week change in views — green bars indicate growing interest
                </Typography>
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart data={momentumData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="week" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine y={0} stroke="#d1d5db" />
                    <Bar dataKey="Change" radius={[2, 2, 0, 0]}>
                      {momentumData.map((entry, i) => (
                        <Cell key={i} fill={entry.Change >= 0 ? COLORS.positive : COLORS.negative} fillOpacity={0.8} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardBody>
            </Card>
          )}

          {/* Chart 3: CTR trend */}
          <Card className="shadow-sm">
            <CardBody className="p-4">
              <Typography variant="small" className="font-semibold text-gray-700 mb-1">
                Click-Through Rate Trend
              </Typography>
              <Typography variant="small" className="text-gray-600 text-xs mb-3">
                Views / impressions — measures how compelling the listing appears in search results
              </Typography>
              <ResponsiveContainer width="100%" height={chartHeight}>
                <LineChart data={ctrChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v * 100).toFixed(1)}%`} />
                  <Tooltip content={<CustomTooltip />} formatter={(v) => fmtPct(v)} />
                  <Line type="monotone" dataKey="CTR" stroke={COLORS.ctr} strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>

          {/* Chart 4: Engagement breakdown */}
          <Card className="shadow-sm">
            <CardBody className="p-4">
              <Typography variant="small" className="font-semibold text-gray-700 mb-1">
                Lead Activity
              </Typography>
              <Typography variant="small" className="text-gray-600 text-xs mb-3">
                Calls, emails, texts, and watchlist adds — direct signals of buyer intent
              </Typography>
              <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart data={engagementData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Calls" fill={COLORS.calls} stackId="a" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Emails" fill={COLORS.emails} stackId="a" />
                  <Bar dataKey="Texts" fill={COLORS.texts} stackId="a" />
                  <Bar dataKey="Watchlists" fill={COLORS.watchlists} stackId="a" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>

          {/* Chart 5: Peer comparison */}
          {peerComparison && (
            <Card className="shadow-sm">
              <CardBody className="p-4">
                <Typography variant="small" className="font-semibold text-gray-700 mb-1">
                  vs {peerComparison.model} Peers ({peerComparison.peerCount} listings)
                </Typography>
                <Typography variant="small" className="text-gray-600 text-xs mb-3">
                  Same-model comparison — highlights under/over-performing metrics
                </Typography>
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart data={peerComparison.data} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="metric" type="category" tick={{ fontSize: 11 }} width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="This Listing" fill={COLORS.listing} radius={[0, 3, 3, 0]} />
                    <Bar dataKey="Peer Avg" fill={COLORS.peer} radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-700">
                  <span>CTR: <strong className="text-gray-700">{fmtPct(peerComparison.ctr.listing)}</strong> vs peer avg <strong>{fmtPct(peerComparison.ctr.peerAvg)}</strong></span>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Chart 6: Featured impact */}
          {featuredImpact && (
            <Card className="shadow-sm">
              <CardBody className="p-4">
                <Typography variant="small" className="font-semibold text-gray-700 mb-1">
                  Featured Listing Impact
                </Typography>
                <Typography variant="small" className="text-gray-600 text-xs mb-3">
                  Average views when featured vs not — {featuredImpact.featuredWeeks} featured weeks, {featuredImpact.notFeaturedWeeks} not featured
                </Typography>
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart data={featuredImpact.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="status" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="Avg Weekly Views" radius={[4, 4, 0, 0]}>
                      <Cell fill={COLORS.featured} />
                      <Cell fill="#d1d5db" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="text-center mt-1">
                  <span className={`text-xs font-semibold ${featuredImpact.lift > 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {featuredImpact.lift > 0 ? '+' : ''}{featuredImpact.lift.toFixed(0)}% lift from featuring
                  </span>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </DialogBody>
    </Dialog>
  );
}

export default WebStatsListingDialog;
